import React, { useState, useEffect } from 'react';
import MetricCard from '../common/MetricCard';
import CyberBadge from '../common/CyberBadge';
import CityPoolHeader from '../common/CityPoolHeader';
import {
  Users,
  Shield,
  MapPin,
  DollarSign,
  Activity,
  Cpu,
  HardDrive,
  Wifi,
  Terminal,
  Server,
  Layers,
  Music,
  BarChart2,
  CheckCircle2,
  Clock,
  ExternalLink,
} from 'lucide-react';

export default function DashboardView({ stats, rooms = [] }) {
  const [activeSub, setActiveSub] = useState('overview');

  // Simulated live VPS metrics
  const [cpuUsage, setCpuUsage] = useState(18.4);
  const [ramUsage, setRamUsage] = useState(4.2);

  // Live Terminal Logs state
  const [terminalLogs, setTerminalLogs] = useState([
    { time: '13:05:10.012', tag: 'OS-KERNEL', msg: 'CPU Load: 18.4% | RAM: 4.2GB / 16GB | IOPS: 4,120/s' },
    { time: '13:05:12.155', tag: 'API-GATEWAY', msg: 'GET /api/admin/dashboard 200 OK (14ms response time)' },
    { time: '13:05:14.901', tag: 'WEBSOCKET', msg: 'Floating audio stream client connected (User ID: #9042)' },
    { time: '13:05:15.310', tag: 'NODE-HEALTH', msg: 'All 4 VPS cluster nodes responding with status OK' },
    { time: '13:05:18.720', tag: 'MONGODB-DB', msg: 'Replica set sync complete (Latency: 0.8ms, Connections: 14)' },
  ]);

  // Telemetry jitter
  useEffect(() => {
    const interval = setInterval(() => {
      setCpuUsage((prev) => parseFloat((15 + Math.random() * 8).toFixed(1)));
      setRamUsage((prev) => parseFloat((4.1 + Math.random() * 0.3).toFixed(1)));

      const now = new Date();
      const timeStr = now.toTimeString().split(' ')[0] + '.' + String(now.getMilliseconds()).padStart(3, '0');
      const tags = ['API-GATEWAY', 'WEBSOCKET', 'NODE-HEALTH', 'MONGODB-DB', 'SYSTEM-GUARD'];
      const msgs = [
        'GET /api/tracks 200 OK (8ms)',
        'Socket ping-pong telemetry keepalive verified',
        'Captains license permission matrix validated',
        'Cache sync complete for live floating audio player',
        'Security TLS 1.3 handshake verified for node MP-01',
      ];
      const randomIdx = Math.floor(Math.random() * tags.length);

      setTerminalLogs((prev) => [
        ...prev.slice(-6),
        { time: timeStr, tag: tags[randomIdx], msg: msgs[randomIdx] },
      ]);
    }, 3500);

    return () => clearInterval(interval);
  }, []);

  const totalUsers = stats?.totalUsers || 9;
  const approvedCaptains = stats?.approvedCaptains || 9;
  const activeRooms = stats?.activeRooms || rooms.length || 5;
  const grossRevenue = stats?.grossRevenue || '3,675';

  return (
    <div className="animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Subnav Navigation */}
      <div className="subnav-pills">
        <button
          className={`subnav-pill ${activeSub === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveSub('overview')}
        >
          <Activity size={14} /> SciFi Telemetry & Wave Charts
        </button>
        <button
          className={`subnav-pill ${activeSub === 'vps' ? 'active' : ''}`}
          onClick={() => setActiveSub('vps')}
        >
          <Cpu size={14} /> VPS Hardware & OS Manager
        </button>
      </div>

      {activeSub === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Cities Section & City Pool Header */}
          <CityPoolHeader />

          {/* Top Metric Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
            <MetricCard title="TOTAL USERS" value={totalUsers} subtext="Platform Registered" change="+12% MoM" changeType="up" icon={Users} />
            <MetricCard title="APPROVED CAPTAINS" value={approvedCaptains} subtext="0 pending approval" change="Verified" changeType="neutral" icon={Shield} />
            <MetricCard title="ACTIVE ROOMS" value={activeRooms} subtext="0 in queue" change="Live Nodes" changeType="up" icon={MapPin} />
            <MetricCard title="GROSS REVENUE" value={`₹${grossRevenue}`} subtext="5% commission cut" change="+18.4%" changeType="up" icon={DollarSign} />
          </div>

          {/* MAIN CHARTS SECTION (3 INTERACTIVE CHARTS) */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
            {/* CHART 1: Multi-Series Wave Area Line Chart */}
            <div className="cyber-card" style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <span className="corner-tl" />
              <span className="corner-tr" />
              <span className="corner-bl" />
              <span className="corner-br" />

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h3 style={{ fontSize: '1.1rem', color: 'var(--neon-cyan)', margin: 0 }}>
                    PROJECT & MODULE TELEMETRY WAVE
                  </h3>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Multi-series growth & revenue curves</span>
                </div>
                <div style={{ display: 'flex', gap: '12px', fontSize: '0.72rem', fontWeight: 700, fontFamily: 'var(--font-mono)' }}>
                  <span style={{ color: 'var(--neon-cyan)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--neon-cyan)' }} /> Active Bookings
                  </span>
                  <span style={{ color: 'var(--neon-purple)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--neon-purple)' }} /> Platform Revenue
                  </span>
                </div>
              </div>

              {/* Multi-Series Wave SVG */}
              <div style={{ height: '230px', width: '100%', position: 'relative' }}>
                <svg viewBox="0 0 600 230" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
                  <defs>
                    <linearGradient id="cyanWave" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00F0FF" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#00F0FF" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="purpleWave" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#A855F7" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="#A855F7" stopOpacity="0" />
                    </linearGradient>
                  </defs>

                  {/* Grid Lines */}
                  <line x1="0" y1="40" x2="600" y2="40" stroke="rgba(0, 240, 255, 0.08)" strokeDasharray="4 4" />
                  <line x1="0" y1="100" x2="600" y2="100" stroke="rgba(0, 240, 255, 0.08)" strokeDasharray="4 4" />
                  <line x1="0" y1="160" x2="600" y2="160" stroke="rgba(0, 240, 255, 0.08)" strokeDasharray="4 4" />

                  {/* Cyan Wave */}
                  <path d="M 0 130 Q 75 50, 150 110 T 300 40 T 450 30 T 600 80 L 600 200 L 0 200 Z" fill="url(#cyanWave)" />
                  <path d="M 0 130 Q 75 50, 150 110 T 300 40 T 450 30 T 600 80" fill="none" stroke="var(--neon-cyan)" strokeWidth="3" />

                  {/* Purple Wave */}
                  <path d="M 0 170 Q 80 120, 160 140 T 320 90 T 480 110 T 600 120 L 600 200 L 0 200 Z" fill="url(#purpleWave)" />
                  <path d="M 0 180 Q 80 120, 160 140 T 320 90 T 480 110 T 600 120" fill="none" stroke="var(--neon-purple)" strokeWidth="2.5" />

                  {/* X Axis Labels */}
                  {['01 Jan', '03 Jan', '05 Jan', '07 Jan', '09 Jan', '11 Jan', '13 Jan', '15 Jan'].map((lbl, idx) => (
                    <text key={idx} x={idx * 75 + 15} y="218" fill="var(--text-muted)" fontSize="10" fontFamily="var(--font-mono)">
                      {lbl}
                    </text>
                  ))}
                </svg>
              </div>
            </div>

            {/* CHART 2: Module Split Donut Ring */}
            <div className="cyber-card" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <span className="corner-tl" />
              <span className="corner-tr" />
              <span className="corner-bl" />
              <span className="corner-br" />

              <div>
                <h3 style={{ fontSize: '1rem', color: 'var(--neon-cyan)', margin: 0 }}>
                  MODULE TRANSACTION SPLIT
                </h3>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Revenue breakdown</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', flex: 1, justifyContent: 'center' }}>
                <div style={{ width: '140px', height: '140px', position: 'relative' }}>
                  <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                    <circle cx="50" cy="50" r="38" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="14" />
                    <circle cx="50" cy="50" r="38" fill="none" stroke="var(--neon-cyan)" strokeWidth="14" strokeDasharray="119 238" strokeDashoffset="0" />
                    <circle cx="50" cy="50" r="38" fill="none" stroke="var(--neon-purple)" strokeWidth="14" strokeDasharray="71 238" strokeDashoffset="-119" />
                    <circle cx="50" cy="50" r="38" fill="none" stroke="var(--neon-green)" strokeWidth="14" strokeDasharray="48 238" strokeDashoffset="-190" />
                  </svg>
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: '1.2rem', fontWeight: 900, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>100%</span>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                    <span style={{ color: 'var(--neon-cyan)', fontWeight: 700 }}>Rap Map (50%)</span>
                    <span style={{ fontFamily: 'var(--font-mono)' }}>₹1,838</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                    <span style={{ color: 'var(--neon-purple)', fontWeight: 700 }}>Fitness (30%)</span>
                    <span style={{ fontFamily: 'var(--font-mono)' }}>₹1,102</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                    <span style={{ color: 'var(--neon-green)', fontWeight: 700 }}>Properties (20%)</span>
                    <span style={{ fontFamily: 'var(--font-mono)' }}>₹735</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CHART 3: Hourly Platform Request Traffic Bar Chart */}
          <div className="cyber-card" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <span className="corner-tl" />
            <span className="corner-tr" />
            <span className="corner-bl" />
            <span className="corner-br" />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h3 style={{ fontSize: '1.05rem', color: 'var(--neon-cyan)', margin: 0 }}>
                  HOURLY PLATFORM API TRAFFIC & REQUEST VOLUME
                </h3>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Peak load distribution over 24 hours</span>
              </div>
              <span className="badge badge-cyan">1,240 REQ/MIN</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', height: '140px', padding: '10px 0', borderBottom: '1px solid var(--border-color)' }}>
              {[35, 20, 15, 10, 8, 12, 45, 65, 80, 95, 70, 85, 90, 100, 88, 75, 92, 84, 60, 48, 40, 30, 25, 20].map((h, i) => (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', height: '100%' }}>
                  <div
                    style={{
                      width: '100%',
                      height: `${h}%`,
                      background: i % 2 === 0 ? 'var(--neon-cyan)' : 'var(--neon-purple)',
                      borderRadius: '3px 3px 0 0',
                      boxShadow: `0 0 10px ${i % 2 === 0 ? 'rgba(0, 240, 255, 0.4)' : 'rgba(168, 85, 247, 0.4)'}`,
                      marginTop: 'auto',
                    }}
                  />
                  <span style={{ fontSize: '0.62rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                    {i < 10 ? `0${i}` : i}h
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* DETAILED FEEDS & CYBER LISTS SECTION (4 RICH LISTS) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
            {/* LIST 1: Recent System Transactions */}
            <div className="cyber-card" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <span className="corner-tl" />
              <span className="corner-tr" />
              <span className="corner-bl" />
              <span className="corner-br" />

              <h4 style={{ fontSize: '0.95rem', color: 'var(--neon-cyan)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <DollarSign size={16} /> Recent Platform Transactions
              </h4>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  { id: 'TXN-90214', name: 'Vijay Nagar Turf Ground', amt: '₹1,200', date: 'Feb 20', status: 'completed' },
                  { id: 'TXN-90215', name: 'Rap Cypher Studio #4', amt: '₹1,500', date: 'Feb 19', status: 'completed' },
                  { id: 'TXN-90216', name: 'Royal Heights 2BHK', amt: '₹975', date: 'Feb 18', status: 'completed' },
                  { id: 'TXN-90217', name: 'Gold Gym Membership', amt: '₹1,100', date: 'Feb 17', status: 'completed' },
                ].map((item) => (
                  <div key={item.id} className="cyber-list-item">
                    <div>
                      <div style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--text-primary)' }}>{item.name}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{item.id} • {item.date}</div>
                    </div>
                    <span style={{ fontSize: '0.9rem', fontWeight: 900, color: 'var(--neon-green)', fontFamily: 'var(--font-mono)' }}>
                      {item.amt}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* LIST 2: Captain Nodes Health Status */}
            <div className="cyber-card" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <span className="corner-tl" />
              <span className="corner-tr" />
              <span className="corner-bl" />
              <span className="corner-br" />

              <h4 style={{ fontSize: '0.95rem', color: 'var(--neon-purple)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Shield size={16} /> Active Captain Nodes
              </h4>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  { name: 'Captain Raj Merchant', city: 'Indore, MP', modules: 'Fitness, Rap Map', ping: '14ms' },
                  { name: 'Vijay Nagar Properties', city: 'Indore, MP', modules: 'Building Rent', ping: '18ms' },
                  { name: 'Malwa Turf Sports', city: 'Bhopal, MP', modules: 'Ground Booking', ping: '22ms' },
                  { name: 'MC Underground Beats', city: 'Indore, MP', modules: 'Loka Hotzone', ping: '12ms' },
                ].map((cap, idx) => (
                  <div key={idx} className="cyber-list-item">
                    <div>
                      <div style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--text-primary)' }}>{cap.name}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{cap.city} • {cap.modules}</div>
                    </div>
                    <span className="badge badge-emerald" style={{ fontSize: '0.65rem' }}>
                      {cap.ping}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* LIST 3: Active Rooms per Module Table */}
            <div className="cyber-card" style={{ display: 'flex', flexDirection: 'column', gap: '14px', gridColumn: '1 / -1' }}>
              <span className="corner-tl" />
              <span className="corner-tr" />
              <span className="corner-bl" />
              <span className="corner-br" />

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h4 style={{ fontSize: '0.95rem', color: 'var(--neon-green)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Layers size={16} /> Platform Module Directory List
                </h4>
                <span className="badge badge-emerald">ALL NODES RESPONDING</span>
              </div>

              <div className="table-container" style={{ border: 'none' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>MODULE NAME</th>
                      <th>APPROVED ROOMS</th>
                      <th>BOOKINGS COUNT</th>
                      <th>MODULE REVENUE</th>
                      <th>STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { name: 'Rap Map (Music / Studio / Hotzones)', count: 2, bookings: 14, rev: '₹1,838', status: 'live' },
                      { name: 'Properties (Flat Rent & Sale)', count: 2, bookings: 8, rev: '₹735', status: 'live' },
                      { name: 'Fitness & Sports (Gyms & Turfs)', count: 1, bookings: 11, rev: '₹1,102', status: 'live' },
                    ].map((row, idx) => (
                      <tr key={idx}>
                        <td style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{row.name}</td>
                        <td style={{ fontWeight: 700 }}>{row.count} Active</td>
                        <td>{row.bookings} total</td>
                        <td style={{ fontWeight: 800, color: 'var(--neon-cyan)', fontFamily: 'var(--font-mono)' }}>{row.rev}</td>
                        <td><CyberBadge status={row.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeSub === 'vps' && (
        <>
          {/* Top Banner: OS Kernel & VPS Status */}
          <div
            className="cyber-card"
            style={{
              background: 'linear-gradient(135deg, rgba(20, 28, 45, 0.9), rgba(12, 17, 28, 0.95))',
              border: '1px solid var(--neon-cyan)',
              boxShadow: '0 0 25px rgba(0, 240, 255, 0.2)',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '20px',
              padding: '20px 24px',
            }}
          >
            <span className="corner-tl" />
            <span className="corner-tr" />
            <span className="corner-bl" />
            <span className="corner-br" />

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div
                style={{
                  width: '46px',
                  height: '46px',
                  borderRadius: '12px',
                  background: 'rgba(0, 240, 255, 0.15)',
                  border: '1px solid rgba(0, 240, 255, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 0 15px rgba(0, 240, 255, 0.3)',
                }}
              >
                <Server size={24} style={{ color: 'var(--neon-cyan)' }} />
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 900, color: 'var(--text-primary)', margin: 0, fontFamily: 'var(--font-title)' }}>
                    LOKA-OS v2.6.4-CYBER
                  </h2>
                  <span className="badge badge-emerald">
                    <span className="pulse-dot" /> VPS ONLINE
                  </span>
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '4px', fontFamily: 'var(--font-mono)' }}>
                  Kernel: Linux x86_64 | Host: node-mp01.rapmap.internal | IP: 104.28.18.90
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', fontSize: '0.8rem', fontFamily: 'var(--font-mono)' }}>
              <div>
                <span style={{ color: 'var(--text-muted)' }}>Load Avg: </span>
                <span style={{ color: 'var(--neon-cyan)', fontWeight: 700 }}>0.42 | 0.38 | 0.35</span>
              </div>
              <div style={{ borderLeft: '1px solid var(--border-color)', paddingLeft: '20px' }}>
                <span style={{ color: 'var(--text-muted)' }}>Uptime: </span>
                <span style={{ color: 'var(--neon-green)', fontWeight: 700 }}>14d 08h 42m (99.98%)</span>
              </div>
            </div>
          </div>

          {/* VPS Hardware Gauges Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '18px' }}>
            <div className="cyber-card" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <span className="corner-tl" />
              <span className="corner-tr" />
              <span className="corner-bl" />
              <span className="corner-br" />

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                  CPU CORE UTILIZATION
                </span>
                <Cpu size={18} style={{ color: 'var(--neon-cyan)' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '1.9rem', fontWeight: 900, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                  {cpuUsage}%
                </span>
                <span style={{ fontSize: '0.75rem', color: 'var(--neon-cyan)', fontWeight: 700 }}>8 vCPU Cores</span>
              </div>
              <div className="vps-progress-bar">
                <div className="vps-progress-fill" style={{ width: `${cpuUsage}%`, background: 'var(--neon-cyan)' }} />
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>Intel Xeon @ 3.40GHz • 14ms latency</div>
            </div>

            <div className="cyber-card" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <span className="corner-tl" />
              <span className="corner-tr" />
              <span className="corner-bl" />
              <span className="corner-br" />

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                  RAM MEMORY ALLOCATION
                </span>
                <HardDrive size={18} style={{ color: 'var(--neon-purple)' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '1.9rem', fontWeight: 900, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                  {ramUsage} GB
                </span>
                <span style={{ fontSize: '0.75rem', color: 'var(--neon-purple)', fontWeight: 700 }}>of 16.0 GB (26%)</span>
              </div>
              <div className="vps-progress-bar">
                <div className="vps-progress-fill" style={{ width: `${(ramUsage / 16) * 100}%`, background: 'var(--neon-purple)' }} />
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>DDR4 ECC 3200MHz • 11.8 GB free</div>
            </div>

            <div className="cyber-card" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <span className="corner-tl" />
              <span className="corner-tr" />
              <span className="corner-bl" />
              <span className="corner-br" />

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                  NVMe STORAGE SSD
                </span>
                <Layers size={18} style={{ color: 'var(--neon-green)' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '1.9rem', fontWeight: 900, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                  42.8 GB
                </span>
                <span style={{ fontSize: '0.75rem', color: 'var(--neon-green)', fontWeight: 700 }}>of 256 GB (16.7%)</span>
              </div>
              <div className="vps-progress-bar">
                <div className="vps-progress-fill" style={{ width: '16.7%', background: 'var(--neon-green)' }} />
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>4,120 Read IOPS • 3,890 Write IOPS</div>
            </div>

            <div className="cyber-card" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <span className="corner-tl" />
              <span className="corner-tr" />
              <span className="corner-bl" />
              <span className="corner-br" />

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                  NETWORK BANDWIDTH
                </span>
                <Wifi size={18} style={{ color: 'var(--neon-amber)' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '1.5rem', fontWeight: 900, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                  12.4 / 48.2
                </span>
                <span style={{ fontSize: '0.72rem', color: 'var(--neon-amber)', fontWeight: 700 }}>Mbps In / Out</span>
              </div>
              <div className="vps-progress-bar">
                <div className="vps-progress-fill" style={{ width: '45%', background: 'var(--neon-amber)' }} />
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>10 Gbps Uplink • Cloudflare CDN Active</div>
            </div>
          </div>

          {/* Live Cyber Terminal Monitor Window */}
          <div className="cyber-card" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <span className="corner-tl" />
            <span className="corner-tr" />
            <span className="corner-bl" />
            <span className="corner-br" />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Terminal size={18} style={{ color: 'var(--neon-cyan)' }} />
                <h3 style={{ fontSize: '1.05rem', color: 'var(--text-primary)', margin: 0 }}>
                  LIVE OS KERNEL & API STREAM MONITOR
                </h3>
              </div>
              <span className="badge badge-cyan">REALTIME STREAM LOGS</span>
            </div>

            <div className="terminal-window">
              {terminalLogs.map((log, idx) => (
                <div key={idx} className="terminal-line">
                  <span className="terminal-time">[{log.time}]</span>
                  <span className="terminal-tag">[{log.tag}]</span>
                  <span>{log.msg}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
